import React from 'react';
import PropTypes from 'prop-types';
import FlagIcon from '../../../components/FlagIcon';
import { useNumberFormat } from '../../../hooks/useNumberFormat';

const ROW_ICON_CLASS =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--off)] text-[var(--n600)]';

function formatAmountDisplay(amount, formatNumber) {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) return formatNumber(0);
  return formatNumber(Number(amount));
}

function formatFeeDisplay(amount, currencyCode, formatNumber) {
  const formatted = formatAmountDisplay(amount, formatNumber);
  return currencyCode === 'EUR' ? `${formatted}\u00a0€` : `${formatted} ${currencyCode}`;
}

function getSendButtonAmount(totalCost, sendAmountNum, currencyCode, formatNumber) {
  const amount = totalCost ?? sendAmountNum ?? 0;
  const formatted = formatAmountDisplay(amount, formatNumber);
  return currencyCode === 'EUR' ? `Send ${formatted}\u00a0€` : `Send ${formatted} ${currencyCode}`;
}

function getDisplaySendAmount(totalCost, sendAmountNum) {
  if (totalCost != null && totalCost > 0) return totalCost;
  if (sendAmountNum > 0) return sendAmountNum;
  return totalCost ?? sendAmountNum ?? 0;
}

function DetailRow({ icon, label, value, subValue, trailing }) {
  return (
    <div className='flex items-center gap-3.5 border-b border-[var(--color-border)] px-[22px] py-4 last:border-b-0'>
      <div className={ROW_ICON_CLASS}>{icon}</div>
      <div className='min-w-0 flex-1'>
        <div className='mb-0.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
          {label}
        </div>
        <div className='text-sm font-medium text-[var(--n800)] font-[family-name:var(--font-ui)]'>{value}</div>
        {subValue && (
          <div className='mt-0.5 text-xs tabular-nums text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>{subValue}</div>
        )}
      </div>
      {trailing && <div className='shrink-0'>{trailing}</div>}
    </div>
  );
}

DetailRow.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  subValue: PropTypes.string,
  trailing: PropTypes.node,
};

const recipientShape = PropTypes.shape({
  name: PropTypes.string,
  currency: PropTypes.string,
});

function SendMoneyReviewStep({
  recipient,
  receiveCurrency,
  sendCurrency,
  amountReceive,
  totalCost,
  sendAmountNum,
  accountDisplay,
  exchangeRateLabel,
  exchangeRateSubLabel,
  countdownDisplay,
  transferFee,
  feePercent,
  paymentReference,
  paymentPurposeLabel,
  onEditAmount,
  onRequote,
  onSend,
  canSend,
  sendDisabledReason,
  quoteRefreshNeeded,
  executingTransfer,
  sendError,
  sendButtonLabel,
  hasQuote,
  isLoadingQuote,
  isQuoteExpired,
}) {
  const { formatNumber } = useNumberFormat();
  const displayReceive = amountReceive ?? 0;
  const displaySend = getDisplaySendAmount(totalCost, sendAmountNum);
  const showRequoteCta = Boolean(quoteRefreshNeeded) && !executingTransfer && typeof onRequote === 'function';

  if (!hasQuote) {
    return (
      <div className='grid gap-[18px]'>
        <div className='rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] px-6 py-12 text-center shadow-[var(--shadow-md)]'>
          {isLoadingQuote ? (
            <>
              <div className='mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-2 border-[var(--n200)] border-t-[var(--n800)]' />
              <p className='text-sm font-medium text-[var(--n800)] font-[family-name:var(--font-ui)]'>Preparing your quote…</p>
              <p className='mt-1.5 text-[13px] text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
                We&apos;ve applied the details from your document.
              </p>
            </>
          ) : (
            <p className='text-sm text-[var(--text-secondary)] font-[family-name:var(--font-ui)]'>
              Generate a quote on the previous step to review your transfer.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='grid gap-[18px]'>
      <div className='relative overflow-hidden rounded-[14px] bg-[var(--n900)] p-7 text-white shadow-[var(--shadow-md)]'>
        <div
          className='pointer-events-none absolute inset-0'
          style={{ background: 'radial-gradient(circle at 100% 0%, rgba(255,255,255,0.05), transparent 60%)' }}
          aria-hidden='true'
        />
        <div className='relative'>
          <div className='mb-2.5 flex items-center justify-between gap-3'>
            <div className='inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/55 font-[family-name:var(--font-ui)]'>
              <span>Recipient gets</span>
              <span
                className='flex h-5 w-5 shrink-0 items-center justify-center rounded-[8px] border border-white/20 bg-white/10'
                aria-hidden='true'
              >
                <FlagIcon currencyCode={receiveCurrency} className='h-2.5 w-3.5 rounded-sm' />
              </span>
            </div>
            <button
              type='button'
              title='Edit amount'
              onClick={onEditAmount}
              className='inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-white font-[family-name:var(--font-ui)] underline decoration-white/35 underline-offset-[3px] transition-opacity hover:opacity-80'
            >
              <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M12 20h9M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z' />
              </svg>
              <span>Edit</span>
            </button>
          </div>
          <div className='mb-3.5 flex items-baseline gap-3.5 font-display text-[56px] font-light leading-none tracking-[-0.025em]'>
            <span>{formatAmountDisplay(displayReceive, formatNumber)}</span>
            <span className='text-[22px] font-normal tracking-[0.04em] text-white/65'>{receiveCurrency}</span>
          </div>
          <div className='my-4 h-px bg-white/10' />
          <div className='flex items-baseline justify-between'>
            <span className='text-[13px] text-white/55 font-[family-name:var(--font-ui)]'>You send</span>
            <span className='font-display text-[22px] font-normal tracking-[-0.01em]'>
              {formatAmountDisplay(displaySend, formatNumber)}{' '}
              <span className='text-sm tracking-[0.05em] text-white/55'>{sendCurrency}</span>
            </span>
          </div>
        </div>
      </div>

      <div className='overflow-hidden rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-md)]'>
        <DetailRow
          icon={<FlagIcon currencyCode={receiveCurrency} className='h-4 w-5 rounded-sm' />}
          label='Currency'
          value={receiveCurrency}
        />

        <DetailRow
          icon={
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M22 21v-2a4 4 0 00-3-3.87M9 11a4 4 0 100-8 4 4 0 000 8zM16 3.13a4 4 0 010 7.75' />
            </svg>
          }
          label='Recipient'
          value={recipient.name || '—'}
          subValue={accountDisplay}
        />

        <DetailRow
          icon={
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M7 16V4M7 4L3 8M7 4l4 4M17 8v12M17 20l4-4M17 20l-4-4' />
            </svg>
          }
          label='Exchange rate'
          value={exchangeRateLabel}
          subValue={exchangeRateSubLabel}
          trailing={
            isQuoteExpired || countdownDisplay ? (
              <div className='text-right leading-tight'>
                {isQuoteExpired ? (
                  <>
                    <div className='mb-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-error,#c4391c)] font-[family-name:var(--font-ui)]'>
                      <svg width='9' height='9' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                        <rect x='4' y='11' width='16' height='10' rx='2' />
                        <path d='M8 11V7a4 4 0 018 0v4' />
                      </svg>
                      Expired
                    </div>
                    <div className='text-sm font-medium text-[var(--color-error,#c4391c)] font-[family-name:var(--font-ui)]'>Quote expired</div>
                  </>
                ) : (
                  <>
                    <div className='mb-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
                      <svg width='9' height='9' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                        <rect x='4' y='11' width='16' height='10' rx='2' />
                        <path d='M8 11V7a4 4 0 018 0v4' />
                      </svg>
                      Locked
                    </div>
                    <div className='text-sm font-medium tabular-nums tracking-[0.01em] text-[var(--n800)] font-[family-name:var(--font-ui)]'>
                      {countdownDisplay}
                    </div>
                  </>
                )}
              </div>
            ) : null
          }
        />

        {transferFee != null && transferFee > 0 && (
          <DetailRow
            icon={
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6' />
              </svg>
            }
            label={feePercent != null ? `Included fees (${feePercent}%)` : 'Included fees'}
            value={formatFeeDisplay(transferFee, sendCurrency, formatNumber)}
          />
        )}

        <div className='border-t border-[var(--color-border)] px-[22px] py-4'>
          <div className='mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
            Description
          </div>
          <div className='text-[13.5px] text-[var(--n800)] font-[family-name:var(--font-ui)]'>{paymentReference?.trim() || '—'}</div>
        </div>

        {paymentPurposeLabel && (
          <div className='border-t border-[var(--color-border)] px-[22px] py-4'>
            <div className='mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)] font-[family-name:var(--font-ui)]'>
              Payment purpose
            </div>
            <div className='text-[13.5px] text-[var(--n800)] font-[family-name:var(--font-ui)]'>{paymentPurposeLabel}</div>
          </div>
        )}
      </div>

      {sendError && !executingTransfer ? (
        <div className='flex items-start gap-3 rounded-lg border border-[#f3c9c4] bg-[#fdf3f2] px-4 py-3.5' role='alert'>
          <div className='mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fbe4e1] text-[#C0392B]'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <circle cx='12' cy='12' r='10' />
              <path d='M12 8v4M12 16h.01' />
            </svg>
          </div>
          <p className='m-0 min-w-0 flex-1 self-center text-sm font-medium leading-[1.5] text-[var(--n800)] font-[family-name:var(--font-ui)]'>{sendError}</p>
        </div>
      ) : null}

      {sendDisabledReason && !canSend && !executingTransfer && !sendError ? (
        <div
          className='flex items-start gap-3 rounded-lg border border-[#f5d0a8] bg-[#fff8f0] px-4 py-3.5'
          role='status'
        >
          <div className='mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fef3e2] text-[#b45309]'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <rect x='4' y='11' width='16' height='10' rx='2' />
              <path d='M8 11V7a4 4 0 018 0v4' />
            </svg>
          </div>
          <div className='min-w-0 flex-1'>
            <p className='m-0 text-sm font-medium text-[var(--n800)] font-[family-name:var(--font-ui)]'>{sendDisabledReason}</p>
            {quoteRefreshNeeded && (
              <button
                type='button'
                onClick={onEditAmount}
                className='mt-2 cursor-pointer border-0 bg-transparent p-0 text-sm font-medium text-[var(--n900)] underline decoration-[var(--n900)]/35 underline-offset-[3px] font-[family-name:var(--font-ui)] hover:opacity-80'
              >
                Or edit amount →
              </button>
            )}
          </div>
        </div>
      ) : null}

      {showRequoteCta ? (
        <button
          type='button'
          onClick={onRequote}
          disabled={isLoadingQuote}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-[22px] py-[13px] text-sm font-medium transition-[background,color,box-shadow,opacity] font-[family-name:var(--font-ui)] ${
            isLoadingQuote
              ? 'cursor-not-allowed border border-[var(--color-border)] bg-[var(--off)] text-[var(--text-tertiary)] shadow-none'
              : 'cursor-pointer border border-[var(--n800)] bg-[var(--n800)] text-white shadow-[var(--shadow-cta)] hover:opacity-90'
          }`}
        >
          {isLoadingQuote ? (
            <span className='h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent' aria-hidden='true' />
          ) : (
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M23 4v6h-6M1 20v-6h6' />
              <path d='M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15' />
            </svg>
          )}
          {isLoadingQuote ? 'Getting new quote…' : 'Get new quote'}
        </button>
      ) : (
        <button
          type='button'
          onClick={onSend}
          disabled={!canSend || executingTransfer}
          title={!canSend && sendDisabledReason ? sendDisabledReason : undefined}
          className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-[22px] py-[13px] text-sm font-medium transition-[background,color,box-shadow,opacity] font-[family-name:var(--font-ui)] ${
            canSend && !executingTransfer
              ? 'cursor-pointer border border-[var(--n800)] bg-[var(--n800)] text-white shadow-[var(--shadow-cta)] hover:opacity-90'
              : 'cursor-not-allowed border border-[var(--color-border)] bg-[var(--off)] text-[var(--text-tertiary)] shadow-none'
          }`}
        >
          {!canSend && !executingTransfer && sendDisabledReason ? (
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <rect x='4' y='11' width='16' height='10' rx='2' />
              <path d='M8 11V7a4 4 0 018 0v4' />
            </svg>
          ) : (
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
              <path d='M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z' />
            </svg>
          )}
          {executingTransfer ? 'Sending…' : sendButtonLabel || getSendButtonAmount(totalCost, sendAmountNum, sendCurrency, formatNumber)}
        </button>
      )}
    </div>
  );
}

SendMoneyReviewStep.propTypes = {
  recipient: recipientShape.isRequired,
  receiveCurrency: PropTypes.string.isRequired,
  sendCurrency: PropTypes.string.isRequired,
  amountReceive: PropTypes.number,
  totalCost: PropTypes.number,
  sendAmountNum: PropTypes.number,
  accountDisplay: PropTypes.string.isRequired,
  exchangeRateLabel: PropTypes.string.isRequired,
  exchangeRateSubLabel: PropTypes.string,
  countdownDisplay: PropTypes.string,
  isQuoteExpired: PropTypes.bool,
  transferFee: PropTypes.number,
  feePercent: PropTypes.number,
  paymentReference: PropTypes.string,
  paymentPurposeLabel: PropTypes.string,
  onEditAmount: PropTypes.func.isRequired,
  onRequote: PropTypes.func,
  onSend: PropTypes.func.isRequired,
  canSend: PropTypes.bool.isRequired,
  sendDisabledReason: PropTypes.string,
  quoteRefreshNeeded: PropTypes.bool,
  executingTransfer: PropTypes.bool,
  sendError: PropTypes.string,
  sendButtonLabel: PropTypes.string,
  hasQuote: PropTypes.bool.isRequired,
  isLoadingQuote: PropTypes.bool,
};

SendMoneyReviewStep.defaultProps = {
  isLoadingQuote: false,
  isQuoteExpired: false,
  sendDisabledReason: null,
  quoteRefreshNeeded: false,
  onRequote: undefined,
};

export default SendMoneyReviewStep;
